import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { LAYOUT_TOAST_TYPES } from '~/src/components/PageLayout/Toasts';
import { useLayoutContext } from '~/src/contexts';
import clioService from '~/src/services/clio';
import type {
  ClioWorkCopyOverview,
  ClioWorkCopyState,
} from '~/src/services/api/clioService';

export const POLL_INTERVAL_MS = 3000;
// A copy is followed every POLL_INTERVAL_MS for its first minute, then less often, and after
// SLOW_POLL_FOR_MS once a minute until it ends: a big copy can run for longer than an hour, and the
// page has to see it end to stop showing it copying. Coming back to the tab refreshes and starts
// following it closely again.
export const SLOW_POLL_INTERVAL_MS = 15000;
export const FAST_POLL_FOR_MS = 60 * 1000;
export const LONG_POLL_INTERVAL_MS = 60 * 1000;
export const SLOW_POLL_FOR_MS = 20 * 60 * 1000;

const isJobRunning = (overview: ClioWorkCopyOverview | null) =>
  overview?.latest_job?.status === 'pending' ||
  overview?.latest_job?.status === 'processing';

// The job this browser just started. Its templates read as copying until the job finishes: the
// worker only marks a template copying once it gets to it.
type StartedJob = { id: number; templateIds: Set<number> };

/**
 * Every copyable template's copy to Clio Work, for the Library. Polls while a copy runs.
 * Does nothing when `enabled` is false. With `copyJobId` (from the done email's link), the
 * overview also gives that copy's templates.
 */
const useClioWorkCopy = (
  orgFprint: string,
  enabled: boolean,
  copyJobId: number | null = null,
) => {
  const { showToast } = useLayoutContext();
  const [overview, setOverview] = useState<ClioWorkCopyOverview | null>(null);
  // The last overview request failed, so `overview` may never arrive (it's only polled while a
  // copy runs).
  const [loadFailed, setLoadFailed] = useState(false);
  const [startedJob, setStartedJob] = useState<StartedJob | null>(null);
  // Bumped when the user comes back to the tab, to start a fresh round of polling.
  const [pollRound, setPollRound] = useState(0);
  // Read when asking, not a reason to ask: leaving the email's filtered view clears it, and that
  // mustn't load the overview again (with its slow Clio Manage check).
  const copyJobIdRef = useRef(copyJobId);
  copyJobIdRef.current = copyJobId;

  // `recheck` asks Clio Manage which copies were deleted there, even if it was asked a moment ago:
  // done when the Library opens or the user comes back to it, not on every poll.
  const refresh = useCallback(
    async (recheck = false) => {
      if (!enabled) {
        return;
      }
      try {
        setOverview(
          await clioService.getWorkTemplateMigrationOverview(
            orgFprint,
            recheck,
            copyJobIdRef.current,
          ),
        );
        setLoadFailed(false);
      } catch (error) {
        // Keep the last known states; the next poll or refresh tries again.
        setLoadFailed(true);
      }
    },
    [enabled, orgFprint],
  );

  useEffect(() => {
    refresh(true);
  }, [refresh]);

  // The started job is over once the server reports it finished, or reports a newer job: another
  // copy can only start once this one has ended.
  useEffect(() => {
    const latestJobId = overview?.latest_job?.id;
    if (
      startedJob &&
      latestJobId !== undefined &&
      (latestJobId > startedJob.id ||
        (latestJobId === startedJob.id && !isJobRunning(overview)))
    ) {
      setStartedJob(null);
    }
  }, [overview, startedJob]);

  const statesById = useMemo(() => {
    const states = new Map<number, ClioWorkCopyState>();
    (overview?.templates || []).forEach((t) => {
      const waiting =
        startedJob?.templateIds.has(t.template_id) && t.status !== 'copying';
      states.set(t.template_id, waiting ? { ...t, status: 'copying' } : t);
    });
    return states;
  }, [overview, startedJob]);

  const copying =
    !!startedJob ||
    isJobRunning(overview) ||
    Array.from(statesById.values()).some((t) => t.status === 'copying');

  useEffect(() => {
    if (!copying) {
      return undefined;
    }
    const startedAt = Date.now();
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const interval = (elapsed: number) => {
      if (elapsed < FAST_POLL_FOR_MS) {
        return POLL_INTERVAL_MS;
      }
      return elapsed < SLOW_POLL_FOR_MS
        ? SLOW_POLL_INTERVAL_MS
        : LONG_POLL_INTERVAL_MS;
    };
    const schedule = () => {
      if (cancelled) {
        return;
      }
      timer = setTimeout(async () => {
        await refresh();
        schedule();
      }, interval(Date.now() - startedAt));
    };
    schedule();
    return () => {
      cancelled = true;
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [copying, refresh, pollRound]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refresh(true);
        setPollRound((round) => round + 1);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [enabled, refresh]);

  // The admin is always emailed when the copy is done. Still asked for explicitly, so a backend
  // that reads "notify_by_email" emails them too.
  const start = useCallback(
    async (templateIds: number[]) => {
      let jobId: number;
      try {
        ({ id: jobId } = await clioService.startWorkTemplateMigration(
          orgFprint,
          templateIds,
          true,
        ));
      } catch (error: any) {
        const status = error?.response?.status;
        let message =
          'We could not start copying your templates. Please try again later.';
        if (status === 417) {
          message =
            'Connect Clio in Settings > Integrations before copying templates.';
        } else if (status === 409) {
          message =
            'Templates are already being copied to Clio Work. Try again once that copy is done.';
        }
        showToast(LAYOUT_TOAST_TYPES.error, { message });
        if (status === 409) {
          // Someone else's copy is running: pick it up, so this page shows it and follows it.
          await refresh();
        }
        return false;
      }
      setStartedJob({ id: jobId, templateIds: new Set(templateIds) });
      await refresh();
      return true;
    },
    [orgFprint, refresh, showToast],
  );

  return { overview, loadFailed, statesById, copying, refresh, start };
};

export default useClioWorkCopy;
